import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import axiosAuth from "../axiosAuth";
import { CREATE_PREDEFINEDTASKASSIGNEMENT, TASK_FILE_UPLOAD } from "./apiRoutes";
import { removeEmptyFields } from "@/lib/utils";

/**
 * -----------------------------
 * Types
 * -----------------------------
 */
export interface Assignment {
  school: string;
  role: string;
  assignTo: string;
}

export interface CreatePredefinedTaskAssignmentPayload {
  taskName: string;
  priority: "LOW" | "MEDIUM" | "HIGH";
  startDate: string;
  endDate: string;
  taskStatus: string;
  taskAttachment?: string | File; // allow File for upload
  taskAttachmentName?: string; // 👈 added for file_name
  taskNotes: string;
  assignments: Assignment[];
}

export interface TaskAssignmentData {
  taskId: string;
  taskName: string;
  priority: string;
  startDate: string;
  endDate: string;
  taskStatus: string;
  taskAttachment: string;
  taskAttachmentName?: string; // 👈 optional file_name in response
  isActive: boolean;
  latestNote: string;
  school: string;
  role: string;
  assignTo: string;
  createdBy: string;
  lastUpdatedBy: string;
  _id: string;
  __v: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePredefinedTaskAssignmentResponse {
  status: number;
  error: boolean;
  count: number;
  issues: any[];
  data: TaskAssignmentData[];
  message: string;
}

/**
 * -----------------------------
 * File Upload Utility
 * -----------------------------
 */
const uploadTaskFileAndGetInfo = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_type", "task_attachment");

  const res = await axiosAuth.post(TASK_FILE_UPLOAD, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const data = res?.data?.data;
  return {
    attachment_id: data?.attachment_id as string,
    file_name: data?.file_name as string,
  };
};

/**
 * -----------------------------
 * Mutation Hook (Final)
 * -----------------------------
 */
export const useCreatePredefinedTaskAssignmentMutation = (
  options?: UseMutationOptions<
    CreatePredefinedTaskAssignmentResponse,
    Error,
    CreatePredefinedTaskAssignmentPayload[]
  >
) => {
  return useMutation({
    mutationFn: async (payload: CreatePredefinedTaskAssignmentPayload[]) => {
      const tasks = await Promise.all(
        payload.map(async (task) => {
          let taskAttachmentUuid = task.taskAttachment;
          let taskAttachmentName = task.taskAttachmentName;

          if (taskAttachmentUuid && typeof taskAttachmentUuid !== "string") {
            const fileInfo = await uploadTaskFileAndGetInfo(taskAttachmentUuid as File);
            taskAttachmentUuid = fileInfo.attachment_id;
            taskAttachmentName = fileInfo.file_name;
          }

          return removeEmptyFields({
            ...task,
            taskAttachment: taskAttachmentUuid,
            taskAttachmentName, // 👈 include file_name
          });
        })
      );

      const response = await axiosAuth.post<CreatePredefinedTaskAssignmentResponse>(
        CREATE_PREDEFINEDTASKASSIGNEMENT,
        { tasks }
      );

      return response.data;
    },
    ...options,
  });
};
